/**
 * MeetingPrep — Pre-meeting briefing panel.
 *
 * Surfaces:
 * - Meeting details (time, title, agenda)
 * - Account health summary
 * - Key people / attendees
 * - Recent interactions
 * - Open questions to address
 * - Open commitments
 * - AI-generated talking points
 * - Relevant memories
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Calendar, Clock, MapPin, Users, TrendingUp, TrendingDown, Minus,
  MessageSquare, Target, HelpCircle, Brain, Activity, ChevronRight,
  Loader2, AlertTriangle, X, RefreshCw, Lightbulb, CheckCircle2,
  Mail, Phone, Video, Building2, Star, AlertCircle
} from 'lucide-react';
import apiClient from '../../lib/apiClient';
import './MeetingPrep.css';

function MeetingPrep({ meetingId, onClose }) {
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['meeting-prep', meetingId],
    queryFn: () => meetingId
      ? apiClient.get(`/api/account-intelligence/meetings/${meetingId}/prep`)
      : apiClient.get('/api/account-intelligence/meetings/upcoming/prep'),
    staleTime: 2 * 60 * 1000, // 2 minutes
    enabled: true,
  });

  const prep = data?.prep;

  if (isLoading) {
    return (
      <div className="mp-container">
        <div className="mp-loading">
          <Loader2 size={24} className="mp-spinner" />
          <span>Preparing your briefing...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mp-container">
        <div className="mp-error">
          <AlertTriangle size={20} />
          <span>Failed to load meeting prep</span>
          <button onClick={() => refetch()}>
            <RefreshCw size={14} /> Retry
          </button>
        </div>
      </div>
    );
  }

  if (!prep) {
    return (
      <div className="mp-container">
        <div className="mp-empty">
          <Calendar size={24} />
          <span>No upcoming meetings found</span>
        </div>
      </div>
    );
  }

  const healthTrendIcon = prep.account?.health?.trend === 'improving' ? TrendingUp
    : prep.account?.health?.trend === 'declining' ? TrendingDown : Minus;
  const HealthTrendIcon = healthTrendIcon;

  return (
    <div className="mp-container">
      {/* Header */}
      <header className="mp-header">
        <div className="mp-header-main">
          <div className="mp-meeting-icon">
            <Calendar size={20} />
          </div>
          <div className="mp-header-text">
            <h1>{prep.meeting.title}</h1>
            <div className="mp-meeting-meta">
              <span className="mp-meta-item">
                <Clock size={12} />
                {prep.meeting.time}
              </span>
              {prep.meeting.duration_minutes && (
                <span className="mp-meta-item">
                  {prep.meeting.duration_minutes} min
                </span>
              )}
              {prep.meeting.location && (
                <span className="mp-meta-item">
                  <MapPin size={12} />
                  {prep.meeting.location}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="mp-header-actions">
          <button
            className="mp-refresh"
            onClick={() => refetch()}
            disabled={isFetching}
            title="Refresh prep"
          >
            <RefreshCw size={16} className={isFetching ? 'mp-spinner' : ''} />
          </button>
          {onClose && (
            <button className="mp-close" onClick={onClose}>
              <X size={16} />
            </button>
          )}
        </div>
      </header>

      {/* Account Overview */}
      {prep.account && (
        <section className="mp-section mp-account">
          <div className="mp-account-header">
            <Building2 size={16} />
            <span className="mp-account-name">{prep.account.name}</span>
            {prep.account.health && (
              <div className={`mp-health-badge mp-health-${getHealthLevel(prep.account.health.overall)}`}>
                <HealthTrendIcon size={12} />
                <span>{prep.account.health.overall}</span>
              </div>
            )}
          </div>

          {prep.account.health && (
            <div className="mp-health-grid">
              <HealthBar label="Relationship" value={prep.account.health.relationship} />
              <HealthBar label="Intelligence" value={prep.account.health.intelligence} />
              <HealthBar label="Momentum" value={prep.account.health.momentum} />
              <HealthBar label="Risk" value={prep.account.health.risk} inverted />
            </div>
          )}

          {prep.engagement && prep.engagement.days_since_last_contact !== null && (
            <div className={`mp-engagement-note ${prep.engagement.days_since_last_contact > 30 ? 'mp-warn' : ''}`}>
              <Activity size={12} />
              <span>
                {prep.engagement.days_since_last_contact === 0
                  ? 'Contacted today'
                  : prep.engagement.days_since_last_contact === 1
                    ? 'Last contact: yesterday'
                    : `Last contact: ${prep.engagement.days_since_last_contact} days ago`}
                {prep.engagement.last_activity && (
                  <> ({prep.engagement.last_activity.type})</>
                )}
              </span>
            </div>
          )}
        </section>
      )}

      {/* Talking Points */}
      {prep.talking_points && prep.talking_points.length > 0 && (
        <section className="mp-section">
          <h2 className="mp-section-title">
            <Lightbulb size={16} />
            Talking Points
          </h2>
          <div className="mp-talking-points">
            {prep.talking_points.map((tp, i) => (
              <div key={i} className={`mp-talking-point mp-priority-${tp.priority}`}>
                <div className="mp-tp-header">
                  <span className="mp-tp-topic">{tp.topic}</span>
                  <span className={`mp-tp-priority mp-${tp.priority}`}>
                    {tp.priority}
                  </span>
                </div>
                <p className="mp-tp-point">{tp.point}</p>
                {tp.rationale && (
                  <p className="mp-tp-rationale">{tp.rationale}</p>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Key People */}
      {prep.key_people && prep.key_people.length > 0 && (
        <section className="mp-section">
          <h2 className="mp-section-title">
            <Users size={16} />
            Key People
          </h2>
          <div className="mp-people-list">
            {prep.key_people.map((person, i) => (
              <div key={i} className="mp-person">
                <div className="mp-person-avatar">
                  {person.name?.charAt(0) || '?'}
                </div>
                <div className="mp-person-info">
                  <span className="mp-person-name">{person.name}</span>
                  {person.title && (
                    <span className="mp-person-title">{person.title}</span>
                  )}
                </div>
                <div className="mp-person-badges">
                  {person.influence === 'decision_maker' && (
                    <span className="mp-badge mp-badge-dm" title="Decision Maker">DM</span>
                  )}
                  {person.relationship === 'champion' && (
                    <span className="mp-badge mp-badge-champion" title="Champion">
                      <Star size={10} />
                    </span>
                  )}
                  {person.relationship === 'blocker' && (
                    <span className="mp-badge mp-badge-blocker" title="Blocker">
                      <AlertCircle size={10} />
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Open Questions */}
      {prep.open_questions && prep.open_questions.length > 0 && (
        <section className="mp-section">
          <h2 className="mp-section-title">
            <HelpCircle size={16} />
            Questions to Address
          </h2>
          <div className="mp-questions-list">
            {prep.open_questions.map((q) => (
              <div key={q.id} className={`mp-question mp-severity-${q.severity}`}>
                <span className="mp-question-text">{q.question}</span>
                <span className={`mp-question-badge mp-${q.severity}`}>
                  {q.category}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Open Commitments */}
      {prep.open_commitments && prep.open_commitments.length > 0 && (
        <section className="mp-section">
          <h2 className="mp-section-title">
            <Target size={16} />
            Open Commitments
          </h2>
          <div className="mp-commitments-list">
            {prep.open_commitments.map((c) => (
              <div key={c.id} className={`mp-commitment ${c.overdue ? 'mp-overdue' : ''}`}>
                <CheckCircle2 size={14} className="mp-commit-icon" />
                <span className="mp-commit-desc">{c.description}</span>
                {c.due_date && (
                  <span className="mp-commit-due">
                    {c.overdue ? 'Overdue: ' : 'Due: '}
                    {new Date(c.due_date).toLocaleDateString()}
                  </span>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Recent Activity */}
      {prep.recent_activities && prep.recent_activities.length > 0 && (
        <section className="mp-section">
          <h2 className="mp-section-title">
            <Activity size={16} />
            Recent Activity
          </h2>
          <div className="mp-activity-list">
            {prep.recent_activities.map((a, i) => (
              <div key={i} className="mp-activity">
                <div className="mp-activity-icon">
                  {getActivityIcon(a.type)}
                </div>
                <div className="mp-activity-content">
                  <span className="mp-activity-title">{a.title || a.type}</span>
                  {a.contact && (
                    <span className="mp-activity-contact">with {a.contact}</span>
                  )}
                </div>
                <span className="mp-activity-date">
                  {formatRelativeDate(a.date)}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Relevant Memories */}
      {prep.relevant_memories && prep.relevant_memories.length > 0 && (
        <section className="mp-section">
          <h2 className="mp-section-title">
            <Brain size={16} />
            Relevant Context
          </h2>
          <div className="mp-memories-list">
            {prep.relevant_memories.map((m) => (
              <div key={m.id} className="mp-memory">
                <span className="mp-memory-type">{m.type}</span>
                <span className="mp-memory-content">{m.content}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Agenda */}
      {prep.meeting.agenda && (
        <section className="mp-section">
          <h2 className="mp-section-title">
            <MessageSquare size={16} />
            Agenda
          </h2>
          <div className="mp-agenda">
            {prep.meeting.agenda}
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="mp-footer">
        <p>Generated at {new Date(prep.generated_at).toLocaleTimeString()}</p>
      </footer>
    </div>
  );
}

// Helper Components

function HealthBar({ label, value, inverted = false }) {
  const level = getHealthLevel(value, inverted);
  return (
    <div className="mp-health-bar">
      <div className="mp-health-label">{label}</div>
      <div className="mp-health-track">
        <div
          className={`mp-health-fill mp-health-${level}`}
          style={{ width: `${value || 0}%` }}
        />
      </div>
      <div className="mp-health-value">{value || 0}</div>
    </div>
  );
}

// Helper Functions

function getHealthLevel(value, inverted = false) {
  if (value === null || value === undefined) return 'unknown';
  const v = inverted ? 100 - value : value;
  if (v >= 70) return 'good';
  if (v >= 40) return 'warning';
  return 'critical';
}

function getActivityIcon(type) {
  switch (type) {
    case 'email': return <Mail size={12} />;
    case 'call': return <Phone size={12} />;
    case 'meeting': return <Video size={12} />;
    default: return <MessageSquare size={12} />;
  }
}

function formatRelativeDate(date) {
  if (!date) return '';
  const d = new Date(date);
  const now = new Date();
  const diffDays = Math.floor((now - d) / 86400000);

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  return d.toLocaleDateString();
}

export default MeetingPrep;
